import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useShop } from "@/context/ShopContext";
import { PAYMENT_LABEL } from "@/lib/constants";
import { ProductCard } from "@/components/shop/ProductCard";
import { StatusBadge } from "@/pages/OrderPage";
import { MyEvents } from "@/components/shop/AccountSections";
import { MyData } from "@/components/shop/MyData";
import { EmailVerifyBanner, B2BStatusBadge } from "@/pages/VerifyEmailPage";
import { CartTimer } from "@/components/shop/CartTimer";
import { WishAlert } from "@/components/shop/WishAlert";
import { ProfileTab } from "@/components/shop/ProfileTab";
import { ProductImage } from "@/components/shop/ProductImage";

const Orders = () => {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/orders/mine").then((r) => setOrders(r.data)); }, []);
  if (!orders) return <p className="py-10 text-stone-500">Caricamento...</p>;
  if (!orders.length) return <p className="py-10 text-stone-600" data-testid="orders-empty">Non hai ancora effettuato ordini. <Link to="/catalogo" className="text-amber-600">Inizia ora →</Link></p>;
  return (
    <div className="space-y-3" data-testid="orders-list">
      {orders.map((o) => (
        <Link key={o.id} to={`/ordine/${o.id}`} data-testid={`order-row-${o.id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-black/10 bg-[#FFFFFF] p-5 transition-colors hover:border-amber-500/40">
          <div className="flex -space-x-3">{o.items.slice(0, 3).map((i) => <ProductImage key={i.product_id} src={i.image} w={300} width={36} height={48} className="h-12 w-9 rounded-md border-2 border-[#FFFFFF] object-cover" />)}</div>
          <div className="flex-1">
            <p className="font-display font-bold">{o.order_number}</p>
            <p className="text-xs text-stone-500">{new Date(o.created_at).toLocaleDateString("it-IT")} · {o.items.length} articoli · {PAYMENT_LABEL[o.payment_method]}</p>
          </div>
          <StatusBadge status={o.status} />
          <span className="font-mono font-bold text-amber-600">{eur(o.total)}</span>
        </Link>
      ))}
    </div>
  );
};

const Wishlist = () => {
  const { wishlist } = useShop();
  const [products, setProducts] = useState(null);
  const load = () => api.get("/wishlist").then((r) => setProducts(r.data.products));
  useEffect(() => { load(); }, [wishlist]);
  if (!products) return <p className="py-10 text-stone-500">Caricamento...</p>;
  if (!products.length) return <p className="py-10 text-stone-600" data-testid="wishlist-empty">La lista desideri è vuota. Tocca il cuore su un prodotto per salvarlo.</p>;
  return <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="wishlist-grid">{products.map((p, i) => <div key={p.id}><ProductCard p={p} index={i} /><WishAlert p={p} onChange={load} /></div>)}</div>;
};

export default function AccountPage() {
  const { user, checkAuth } = useAuth();
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "ordini";
  return (
    <div className="mx-auto max-w-[1200px] px-4 py-8 sm:px-6 sm:py-12">
      <p className="text-xs uppercase tracking-[0.2em] text-amber-600">Il mio account</p>
      <h1 className="mt-2 font-display text-4xl font-bold" data-testid="account-title">Ciao, {user.name}</h1>
      <p className="flex flex-wrap items-center gap-2 text-sm text-stone-600">{user.email} <B2BStatusBadge /></p>
      <div className="mt-4 space-y-3"><EmailVerifyBanner testid="account-verify-banner" /><CartTimer testid="account-cart-timer" /></div>
      <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border-2 border-black bg-violet-500 px-5 py-3 text-white shadow-[4px_4px_0_#000]" data-testid="account-credit">
        <span className="text-sm font-semibold">Credito negozio</span>
        <span className="font-mono text-xl font-bold">{eur(user.store_credit || 0)}</span>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="mt-8">
        <TabsList className="h-auto w-full justify-start overflow-x-auto bg-[#F3E9D7] sm:w-auto">
          <TabsTrigger value="ordini" data-testid="account-tab-orders">I miei ordini</TabsTrigger>
          <TabsTrigger value="wishlist" data-testid="account-tab-wishlist">Lista desideri</TabsTrigger>
          <TabsTrigger value="eventi" data-testid="account-tab-events">I miei eventi</TabsTrigger>
          <TabsTrigger value="profilo" data-testid="account-tab-profile">Profilo</TabsTrigger>
          <TabsTrigger value="dati" data-testid="account-tab-data">I miei dati</TabsTrigger>
        </TabsList>
        <TabsContent value="ordini" className="mt-6"><Orders /></TabsContent>
        <TabsContent value="wishlist" className="mt-6"><Wishlist /></TabsContent>
        <TabsContent value="eventi" className="mt-6"><MyEvents /></TabsContent>
        <TabsContent value="profilo" className="mt-6"><ProfileTab /></TabsContent>
        <TabsContent value="dati" className="mt-6"><MyData /></TabsContent>
      </Tabs>
    </div>
  );
}
